import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { getCookie, setCookie } from '@/lib/cookies'

import { I18nContext, type I18nContextValue } from './context'
import { en } from './locales/en'
import { id } from './locales/id'
import { DEFAULT_LOCALE, isLocale, type Locale, type Localized } from './types'

const COOKIE_KEY = 'gmt2027_locale'

const DICTIONARIES = { id, en } as const

/**
 * The site is Indonesian-first: everyone lands on `id` unless they have
 * explicitly chosen otherwise before. Browser language is deliberately *not*
 * consulted — the theme and audience are Indonesian, and the choice is asked
 * for directly on first visit (`LanguagePrompt`) and always one tap away in
 * the header afterwards.
 */
function detectLocale(): Locale {
  const stored = getCookie(COOKIE_KEY)
  return isLocale(stored) ? stored : DEFAULT_LOCALE
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale)
  // Whether a choice was already persisted *before* this render — read once,
  // synchronously, so the first-visit prompt never flashes for a returning
  // visitor.
  const [hasChosenLocale, setHasChosenLocale] = useState<boolean>(() =>
    isLocale(getCookie(COOKIE_KEY)),
  )

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const persist = useCallback((next: Locale) => {
    setLocaleState(next)
    setHasChosenLocale(true)
    setCookie(COOKIE_KEY, next)
  }, [])

  const setLocale = useCallback((next: Locale) => persist(next), [persist])
  const toggleLocale = useCallback(() => persist(locale === 'id' ? 'en' : 'id'), [locale, persist])

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      toggleLocale,
      hasChosenLocale,
      t: DICTIONARIES[locale],
      pick: <T,>(field: Localized<T>) => field[locale],
    }),
    [locale, setLocale, toggleLocale, hasChosenLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
